export interface GoNoGoAnalysis {
  score: number;
  reasoning: string;
  risks: string[];
  opportunities: string[];
  recommendation: 'GO' | 'NO-GO' | 'CAUTION';
  isFallback?: boolean;
}

export interface MarketResearch {
  competitors: string[];
  marketTrends: string[];
  pricingBenchmarks: string;
  strategicAdvice: string;
  isFallback?: boolean;
}

export interface BidGeneration {
  proposal: string;
  pricingStrategy: string;
  complianceChecklist: string[];
  isFallback?: boolean;
}

export interface DocumentSummary {
  summary: string;
  sections: { title: string; content: string }[];
  keyRequirements: string[];
  estimatedBudget: string;
  dueDates: string;
  isFallback?: boolean;
}

// ===============================================================
// Robust Local Client-Side Fallback Generators (Quota/Network Safe)
// ===============================================================

function fallbackAnalyzeOpportunity(solicitation: any, vendor: any): GoNoGoAnalysis {
  const companyName = vendor?.companyName || "Vendor";
  const solTitle = solicitation?.title || "Project";
  
  let score = 78;
  let recommendation: "GO" | "NO-GO" | "CAUTION" = "GO";
  const risks: string[] = [];
  const opportunities: string[] = [];
  
  if (solicitation?.category && vendor?.categories) {
    const isMatched = vendor.categories.some((cat: string) => 
      solicitation.category.toLowerCase().includes(cat.toLowerCase())
    );
    if (!isMatched) {
      score = 48;
      recommendation = "CAUTION";
      risks.push(`Capability gap: Vendor categories do not explicitly include '${solicitation.category}'.`);
    } else {
      score = 88;
      opportunities.push(`Strategic Match: High alignment with vendor's core offering in ${solicitation.category}.`);
    }
  }

  risks.push(
    "Contractual compliance burdens and potential penalty clauses.",
    "Tough timelines for response submissions and deployment phases."
  );

  opportunities.push(
    "High potential for recurring contract extension options.",
    `Establishes strong footprint within ${solicitation?.agency || "state agency"}.`
  );

  const reasoning = `[CLIENT-SIDE SAFE FALLBACK] Conducted analytical fit assessment of '${solTitle}' for '${companyName}'. Discovered several key synergies aligned with technical capability areas. Suggested action is to proceed with caution due to tight performance timelines.`;

  return {
    score,
    reasoning,
    risks,
    opportunities,
    recommendation,
    isFallback: true
  };
}

function getRfxItemsText(solicitation: any): string {
  const rows = solicitation?.details?.response?.rows || solicitation?.rows || [];
  if (!rows || rows.length === 0) {
    return "No custom line items specified in solicitation details.";
  }
  
  let text = "### 4. RFX Item Specifications & Custom Delivery Solution\n";
  text += "We have fully analyzed the required line items and guarantee exact matching to the requested specifications:\n\n";
  text += "| Line ID | Item Type | Specification / Name | Description | Requested Qty & Unit | Our Solution Compliance |\n";
  text += "|---|---|---|---|---|---|\n";
  
  rows.forEach((row: any, idx: number) => {
    const lineId = row[2] || idx + 1;
    const itemType = row[4] || "Required Item";
    const itemName = row[5] || "Item Specification";
    const itemDesc = row[6] || "N/A";
    const qtyUnit = row[10] ? `${row[10]} ${row[11] || "Each"}` : "As Required";
    
    // Clean strings of markdown table chars
    const cleanType = String(itemType).replace(/\|/g, '\\|');
    const cleanName = String(itemName).replace(/\|/g, '\\|');
    const cleanDesc = String(itemDesc).replace(/\|/g, '\\|').substring(0, 100) + (String(itemDesc).length > 100 ? "..." : "");
    const cleanQty = String(qtyUnit).replace(/\|/g, '\\|');
    
    text += `| **${lineId}** | ${cleanType} | ${cleanName} | ${cleanDesc} | ${cleanQty} | **100% Compliant** - Direct supply of specified item |\n`;
  });
  
  return text;
}

function fallbackGenerateBid(solicitation: any, vendor: any): BidGeneration {
  const companyName = vendor?.companyName || "Our Company";
  const solTitle = solicitation?.title || "Solicitation RFP";
  const solId = solicitation?.solicitationId || "RFP-101";
  const rfxItemsText = getRfxItemsText(solicitation);

  const proposal = `# Bid Proposal: ${solTitle}
**Reference ID**: ${solId}  
**Prepared For**: ${solicitation?.agency || "State Procurement Office"}  
**Prepared By**: ${companyName}  

## 1. Executive Summary
${companyName} is pleased to submit this comprehensive, high-quality proposal in response to the ${solTitle} solicitation. We bring deep, proven domain expertise, a robust operational infrastructure, and a highly qualified team ready to ensure project success on schedule and within budget.

## 2. Company Overview & Credentials
- **Company Name**: ${companyName}
- **Primary Business Scope**: State and local government solutions
- **Qualifications**: Demonstrated compliance, modern delivery models, and 100% service-level agreement tracking.

## 3. Scope of Work & Delivery Methodology
We utilize an agile, phased delivery approach to fulfill the goals of this solicitation:
- **Phase 1: Mobilization & Planning** - Initial setup, resource allocation, and kickoff.
- **Phase 2: Execution & Quality Assurance** - Implementation of custom specifications and ongoing audits.
- **Phase 3: Delivery & Post-Implementation Support** - Successful sign-off and continuous monitoring.

${rfxItemsText}

## 4. Quality Management & Compliance Control
We implement rigorous quality standards to ensure all contract requirements are met or exceeded. All personnel assigned to this contract undergo background checks and hold industry-standard certifications.
`;

  return {
    proposal,
    pricingStrategy: `Competitive cost-plus pricing strategy optimized for public procurement. Estimated value: $${solicitation?.pricing || 25000}.`,
    complianceChecklist: [
      "Fulfill all specific delivery line items with 100% compliance",
      "Assign certified personnel and account manager",
      "Provide weekly status reporting and quality audits",
      "Maintain active business registrations and dynamic insurance policies"
    ],
    isFallback: true
  };
}

function fallbackConductMarketResearch(solicitation: any): MarketResearch {
  const solTitle = solicitation?.title || "General RFP";
  const solAgency = solicitation?.agency || "State Agencies";
  
  return {
    competitors: [
      "GovSolutions Inc.",
      "Apex Procurement Group",
      "Statewide Enterprise Partners"
    ],
    marketTrends: [
      `Increased demand for digital-first frameworks within ${solAgency}.`,
      "Shift towards value-based multi-year contract vehicles.",
      "Strict compliance requirements around secure data storage and reporting."
    ],
    pricingBenchmarks: "Historic pricing databases for similar projects show average contract values between $50,000 and $250,000. Recommend bidding mid-to-lower range for competitive edge.",
    strategicAdvice: `Highlight unique localized expertise, reference strong past performance references with state agencies, and propose a transparent pricing breakdown.`,
    isFallback: true
  };
}

function fallbackSummarizeDocument(fileName: string): DocumentSummary {
  return {
    summary: `This is a synthesized summary of the document: "${fileName}". The document outlines the critical scopes of work, compliance frameworks, technical requirements, and legal conditions required under the solicitation.`,
    sections: [
      { title: "1. Scope of Work", content: "Details the core tasks, expectations, and functional deliverables expected under the contract." },
      { title: "2. Technical & Compliance Requirements", content: "Lists mandatory standards, certifications, and response templates to be completed." },
      { title: "3. Submission & Evaluation", content: "Specifies evaluation criteria, pricing components, and instructions for delivery." }
    ],
    keyRequirements: [
      "Strict compliance with state cybersecurity standards",
      "Completion of all mandatory cost proposals and technical worksheets",
      "Submission prior to the specified end date and time"
    ],
    estimatedBudget: "See general pricing sections of the solicitation.",
    dueDates: "Refer to the main solicitation summary page.",
    isFallback: true
  };
}

export const geminiService = {
  async analyzeOpportunity(solicitation: any, vendor: any): Promise<GoNoGoAnalysis> {
    const response = await fetch("/api/analyze-opportunity", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ solicitation, vendor }),
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`API Error: ${response.statusText} - ${errText}`);
    }

    return await response.json();
  },

  async generateBid(solicitation: any, vendor: any, historicalBids: any[]): Promise<BidGeneration> {
    const response = await fetch("/api/generate-bid", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ solicitation, vendor, historicalBids }),
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`API Error: ${response.statusText} - ${errText}`);
    }

    return await response.json();
  },

  async conductMarketResearch(solicitation: any): Promise<MarketResearch> {
    const response = await fetch("/api/conduct-market-research", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ solicitation }),
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`API Error: ${response.statusText} - ${errText}`);
    }

    return await response.json();
  },

  async optimizeTemplate(title: string, category: string, content: string, instructions: string): Promise<{ optimizedContent: string; improvements: string[] }> {
    const response = await fetch("/api/optimize-template", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ title, category, content, instructions }),
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`API Error: ${response.statusText} - ${errText}`);
    }

    return await response.json();
  },

  async summarizeDocument(fileName: string, originalUrl: string): Promise<DocumentSummary> {
    const response = await fetch("/api/summarize-document", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ fileName, originalUrl }),
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`API Error: ${response.statusText} - ${errText}`);
    }

    return await response.json();
  }
};
